import { diffDraft, type DraftErrors, type ProjectionDraft, type ProjectionRecord } from '@/lib/projection-cell'

/** An existing row in edit mode: the snapshot taken when editing started, and the draft. */
export interface RowEdit {
  base: ProjectionDraft
  draft: ProjectionDraft
}

export interface NewRow {
  key: string
  draft: ProjectionDraft
}

/** Local UI state of the "Manage projections" modal — ids and drafts, never server rows. */
export interface ManageProjectionsState {
  /** Rows in edit mode, by projection id. */
  edits: Record<string, RowEdit>
  /** Unsaved new rows, newest first (rendered at the top). */
  newRows: NewRow[]
  /** By row key (projection id or new-row key). */
  errors: Record<string, DraftErrors>
  /** Row keys whose write is in flight. */
  saving: Record<string, true>
  selected: string[]
  pendingDeleteId: string | null
  /** The "discard unsaved changes" confirmation is open. */
  confirmDiscard: boolean
  /** Source of new-row keys; only ever grows. */
  nextNewRow: number
}

/** Prefix of new-row keys, so they can never collide with a projection id. */
const NEW_ROW_PREFIX = 'new-'

export const initialManageProjectionsState: ManageProjectionsState = {
  edits: {},
  newRows: [],
  errors: {},
  saving: {},
  selected: [],
  pendingDeleteId: null,
  confirmDiscard: false,
  nextNewRow: 1,
}

export type ManageProjectionsAction =
  | { type: 'startEdit'; id: string; base: ProjectionDraft }
  | { type: 'addRow'; draft: ProjectionDraft }
  | { type: 'changeDraft'; key: string; patch: Partial<ProjectionDraft> }
  | { type: 'setErrors'; key: string; errors: DraftErrors }
  | { type: 'discardRow'; key: string }
  | { type: 'writeStarted'; key: string }
  /** `leaveEditMode`: the row's draft was saved; otherwise any draft stays, e.g. for a retry. */
  | { type: 'writeFinished'; key: string; leaveEditMode: boolean }
  | { type: 'setSelected'; ids: string[] }
  | { type: 'requestDelete'; id: string }
  | { type: 'closeDelete' }
  /** The delete of `id` finished: close its confirmation, but not one opened since for another row. */
  | { type: 'deleteFinished'; id: string }
  | { type: 'rowRemoved'; id: string }
  | { type: 'requestDiscardAll' }
  | { type: 'closeDiscardAll' }

function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  if (!(key in record)) return record
  const { [key]: _removed, ...rest } = record
  return rest
}

/**
 * Drops the errors of fields whose value actually changed (a DateField re-reporting
 * `''` mid-typing is no change); others stay until the next Apply. Returns `errors`
 * itself when nothing was dropped.
 */
function clearChangedErrors(
  errors: DraftErrors | undefined,
  before: ProjectionDraft,
  after: ProjectionDraft,
): DraftErrors | undefined {
  if (!errors) return errors
  const changed = (Object.keys(errors) as Array<keyof ProjectionDraft>).filter(
    (field) => !Object.is(before[field], after[field]),
  )
  if (changed.length === 0) return errors
  const rest = { ...errors }
  for (const field of changed) delete rest[field]
  return rest
}

function leaveEditMode(state: ManageProjectionsState, key: string): ManageProjectionsState {
  return {
    ...state,
    edits: without(state.edits, key),
    newRows: state.newRows.filter((row) => row.key !== key),
    errors: without(state.errors, key),
  }
}

/** The key the next `addRow` will use — so the caller can focus it. */
export function nextNewRowKey(state: ManageProjectionsState): string {
  return `${NEW_ROW_PREFIX}${state.nextNewRow}`
}

export function manageProjectionsReducer(
  state: ManageProjectionsState,
  action: ManageProjectionsAction,
): ManageProjectionsState {
  switch (action.type) {
    case 'startEdit':
      if (action.id in state.edits) return state
      return {
        ...state,
        edits: { ...state.edits, [action.id]: { base: action.base, draft: action.base } },
        // A row in edit mode is not selectable: a bulk write would replace the
        // values under the user's draft without them noticing.
        selected: state.selected.filter((id) => id !== action.id),
      }
    case 'addRow':
      return {
        ...state,
        newRows: [{ key: nextNewRowKey(state), draft: action.draft }, ...state.newRows],
        nextNewRow: state.nextNewRow + 1,
      }
    case 'changeDraft': {
      const edit = state.edits[action.key]
      const draft = edit?.draft ?? state.newRows.find((row) => row.key === action.key)?.draft
      if (!draft) return state
      const next = { ...draft, ...action.patch }
      const errors = clearChangedErrors(state.errors[action.key], draft, next)
      const withErrors = errors === state.errors[action.key] ? state.errors : { ...state.errors, [action.key]: errors ?? {} }
      if (edit) {
        return {
          ...state,
          edits: { ...state.edits, [action.key]: { ...edit, draft: next } },
          errors: withErrors,
        }
      }
      return {
        ...state,
        newRows: state.newRows.map((row) => (row.key === action.key ? { ...row, draft: next } : row)),
        errors: withErrors,
      }
    }
    case 'setErrors':
      return { ...state, errors: { ...state.errors, [action.key]: action.errors } }
    case 'discardRow':
      return leaveEditMode(state, action.key)
    case 'writeStarted':
      return { ...state, saving: { ...state.saving, [action.key]: true } }
    case 'writeFinished': {
      const next = { ...state, saving: without(state.saving, action.key) }
      return action.leaveEditMode ? leaveEditMode(next, action.key) : next
    }
    case 'setSelected':
      return { ...state, selected: action.ids.filter((id) => !(id in state.edits)) }
    case 'requestDelete':
      return { ...state, pendingDeleteId: action.id }
    case 'closeDelete':
      return { ...state, pendingDeleteId: null }
    case 'deleteFinished':
      return state.pendingDeleteId === action.id ? { ...state, pendingDeleteId: null } : state
    case 'rowRemoved':
      return {
        ...leaveEditMode(state, action.id),
        selected: state.selected.filter((id) => id !== action.id),
      }
    case 'requestDiscardAll':
      return { ...state, confirmDiscard: true }
    case 'closeDiscardAll':
      return { ...state, confirmDiscard: false }
    default: {
      const unreachable: never = action
      return unreachable
    }
  }
}

/**
 * Unsaved changes closing the modal would lose: new rows plus changed edits of
 * still-listed rows (`rowIds`). Rows whose save is in flight are not counted —
 * that write lands either way.
 */
export function unsavedCount(state: ManageProjectionsState, rowIds: ReadonlySet<string>): number {
  const newRows = state.newRows.filter((row) => !state.saving[row.key]).length
  const changedEdits = Object.entries(state.edits).filter(
    ([id, edit]) =>
      rowIds.has(id) && !state.saving[id] && Object.keys(diffDraft(edit.base, edit.draft)).length > 0,
  ).length
  return newRows + changedEdits
}

/** The selected ids that are still listed, in list order. */
export function visibleSelection(state: ManageProjectionsState, rowIds: readonly string[]): string[] {
  const selected = new Set(state.selected)
  return rowIds.filter((id) => selected.has(id))
}

/**
 * One row of the modal's table: a saved projection in view mode, a saved one
 * in edit mode, or an unsaved new one. `key` is the projection id or the
 * new-row key — the table's row key and the prefix of the row's control ids.
 */
export type ManageProjectionsRow =
  | { kind: 'view'; key: string; record: ProjectionRecord }
  | { kind: 'edit'; key: string; draft: ProjectionDraft }
  | { kind: 'new'; key: string; draft: ProjectionDraft }

/**
 * The table's rows for one page: every new row on top (on every page, newest
 * first), then the page's saved rows, each in edit mode when it has a draft.
 */
export function tableRows(state: ManageProjectionsState, pageRows: readonly ProjectionRecord[]): ManageProjectionsRow[] {
  const newRows = state.newRows.map((row): ManageProjectionsRow => ({ kind: 'new', key: row.key, draft: row.draft }))
  const savedRows = pageRows.map((record): ManageProjectionsRow => {
    const edit = state.edits[record.id]
    return edit ? { kind: 'edit', key: record.id, draft: edit.draft } : { kind: 'view', key: record.id, record }
  })
  return [...newRows, ...savedRows]
}
